import { FC } from 'react'
import { Group } from '@wellesley/openapi'
import { toast } from '@wellesley/system-toast'
import { usePostApiV1GroupsByGroupIdJoinMutation } from '../../../api/groups'
import { JoinButton } from './JoinButton'
import RulesModal from '../../modals/RulesModal'
import useGroupRules from '../../../hooks/useGroupRules'
import { logger } from 'helpers/logger'
import useGroupMember from '../../../hooks/useGroupMember'
import { useDeleteApiV1GroupsByGroupIdJoinMutation } from '../../../api'
import { useEventState } from 'hooks/state/useEventState'

type GroupJoinButtonProps = {
  group: Group
  inviteLink?: boolean
  onJoinSuccess?: () => void
  onCancelSuccess?: () => void
  onVisit?: () => void
  onOpenRulesModalChange?: (opened: boolean) => void
  className?: string
}

const GroupJoinButton: FC<GroupJoinButtonProps> = ({
  group,
  onJoinSuccess,
  onCancelSuccess,
  onVisit,
  inviteLink,
  className,
  onOpenRulesModalChange,
}) => {
  const { groupId } = group
  const { groupMember } = useGroupMember(groupId)
  const [joinGroup] = usePostApiV1GroupsByGroupIdJoinMutation()
  const [cancelRequest] = useDeleteApiV1GroupsByGroupIdJoinMutation()
  const { rules } = useGroupRules({ groupId: groupId })
  const [showRulesModal, setShowRulesModal] = useEventState(
    false,
    onOpenRulesModalChange,
  )

  const handleJoinGroup = async () => {
    try {
      await joinGroup({ groupId: groupId }).unwrap()

      if (
        group.joinMode === 'OPEN' ||
        (group.joinMode === 'INVITE_ONLY' && inviteLink)
      ) {
        toast.success("You've joined the group")
      } else {
        toast.success('Join request sent')
      }

      onJoinSuccess?.()
    } catch (error) {
      logger.error(JSON.stringify(error))
    }
  }

  const handleAgreeToRules = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    setShowRulesModal(false)
    void handleJoinGroup()
  }

  const handleDismissRulesModal = () => {
    setShowRulesModal(false)
  }

  const handleCancelRequest = async (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    try {
      await cancelRequest({ groupId: groupId }).unwrap()
      toast.success('Join request cancelled')
      onCancelSuccess?.()
    } catch (error) {
      logger.error(JSON.stringify(error))
    }
  }

  const handleOpenRulesModal = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (rules?.length) {
      setShowRulesModal(true)
    } else {
      void handleJoinGroup()
    }
  }

  return (
    <>
      <JoinButton
        group={group}
        inviteLink={inviteLink}
        onJoin={handleOpenRulesModal}
        onVisit={onVisit}
        onCancelRequest={(e) => void handleCancelRequest(e)}
        className={className}
        membershipState={groupMember?.state}
      />

      <RulesModal
        open={showRulesModal}
        onClose={handleDismissRulesModal}
        onAgree={handleAgreeToRules}
        groupId={groupId}
        joinMode={group.joinMode}
        inviteLink={inviteLink}
      />
    </>
  )
}

export default GroupJoinButton
